import React, { useEffect } from 'react';
import { Card, Row, Col, Tag, Space, Button, Typography } from 'antd';
import dayjs from 'dayjs';
import type { RouteViewModelStructure, RouteViewModelStop } from '../../../types/routeViewModel';
import { DataTable } from '../../../ui';
import { isSequentialStopsReadModeEnabled } from '../../../features/sequentialStops/sequentialStopsReadFlags';
import { logSequentialStopsReadShadow, sortStopsByPosition } from '../../../features/sequentialStops/sequentialStopsOrdering';
import {
  buildRouteDirectionLabel,
  buildRouteStopPresentation,
  buildRouteStopPresentationContext,
  formatRouteStopSourceDisplay,
  type RouteStopPresentationContext,
  type RouteStopPresentationInput
} from '../../../features/routes/routeSummaryProjection';
import {
  getStopExecutionActualAt,
  getStopExecutionState,
} from '../../../features/routes/routeViewExecutionHelpers';

const { Text } = Typography;

/** Склонение «N точка / точки / точек» для скобок в summary */
function stopsCountPhrase(n: number): string {
  const abs = Math.abs(n);
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  let word = 'точек';
  if (mod10 === 1 && mod100 !== 11) word = 'точка';
  else if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) word = 'точки';
  return `${n} ${word}`;
}

function structureSummaryLine(directionLabel: string, stopCount: number): string {
  const dir = directionLabel.trim() || '—';
  return `${dir} (${stopsCountPhrase(stopCount)})`;
}

/** @deprecated use buildRouteStopPresentationContext from routeSummaryProjection */
export function buildStopPresentationContext(params: {
  applications: Record<string, unknown>[];
  loadingWarehouse: Record<string, unknown> | null;
  unloadingWarehouse: Record<string, unknown> | null;
  routeConsigneeName?: string | null;
}): RouteStopPresentationContext {
  return buildRouteStopPresentationContext({
    applications: params.applications as RouteStopPresentationContext['applications'],
    loadingWarehouse: params.loadingWarehouse as RouteStopPresentationContext['loadingWarehouse'],
    unloadingWarehouse: params.unloadingWarehouse as RouteStopPresentationContext['unloadingWarehouse'],
    routeConsigneeName: params.routeConsigneeName ?? null
  });
}

/** @deprecated use buildRouteStopPresentation — kept for tests/imports */
export function stopSourceLabel(
  s: RouteViewModelStop,
  applications: Record<string, unknown>[],
  loadingWarehouse: Record<string, unknown> | null,
  unloadingWarehouse: Record<string, unknown> | null
): string {
  const ctx = buildStopPresentationContext({ applications, loadingWarehouse, unloadingWarehouse });
  return buildRouteStopPresentation(s as RouteStopPresentationInput, ctx).sourceLabel;
}

/** Полный адрес точки: стоп → заявка (погрузка/выгрузка) → склад. */
export function stopFullAddress(
  s: RouteViewModelStop,
  applications: Record<string, unknown>[],
  loadingWarehouse: Record<string, unknown> | null,
  unloadingWarehouse: Record<string, unknown> | null,
  stopLoadUnload: 'loading' | 'unloading'
): string {
  const trimStr = (x: unknown) => (x != null && String(x).trim() ? String(x).trim() : '');
  const fromStop = trimStr(s.address);
  if (fromStop) return fromStop;

  const pt = String(s.pointType || '').toLowerCase();
  const pid = s.pointId != null ? Number(s.pointId) : NaN;

  if (pt === 'application' && Number.isFinite(pid)) {
    const appField = stopLoadUnload === 'loading' ? 'loadingAddress' : 'unloadingAddress';
    for (const row of applications) {
      const app = row.application as Record<string, unknown> | undefined;
      const aid =
        row.applicationId != null
          ? Number(row.applicationId)
          : app?.id != null
            ? Number(app.id)
            : NaN;
      if (Number.isFinite(aid) && aid === pid) {
        const a = trimStr(app?.[appField]);
        if (a) return a;
      }
    }
  }

  if (pt === 'warehouse' && Number.isFinite(pid)) {
    const fromWh = (wh: Record<string, unknown> | null) => {
      if (!wh || Number(wh.id) !== pid) return '';
      return trimStr(wh.address);
    };
    const w = fromWh(loadingWarehouse) || fromWh(unloadingWarehouse);
    if (w) return w;
  }

  return '—';
}

export interface RouteStructureBlockProps {
  model: RouteViewModelStructure;
  /** Из view.operation.direction.label (напр. «Колпино → Тында») */
  routeDirectionLabel: string;
  routeLoadingCity?: string | null;
  routeUnloadingCity?: string | null;
  applications: Record<string, unknown>[];
  loadingWarehouse: Record<string, unknown> | null;
  unloadingWarehouse: Record<string, unknown> | null;
  routeStatus: string;
  loadingStop: boolean;
  unloadingStop: boolean;
  showCargoSummary?: boolean;
  onRequestLoad: (stopId: number) => void;
  onRequestUnload: (stopId: number) => void;
  /** Подсветка и прокрутка к точке (deep link из истории задач). */
  focusedStopId?: number | null;
  stopExecutionIndex?: Map<
    number,
    { label: string; isProcessed: boolean; canLoad: boolean; canUnload: boolean }
  >;
}

const RouteStructureBlock: React.FC<RouteStructureBlockProps> = ({
  model,
  routeDirectionLabel,
  routeLoadingCity,
  routeUnloadingCity,
  applications,
  loadingWarehouse,
  unloadingWarehouse,
  routeStatus,
  loadingStop,
  unloadingStop,
  showCargoSummary = true,
  onRequestLoad,
  onRequestUnload,
  focusedStopId,
  stopExecutionIndex
}) => {
  const stops = sortStopsByPosition((model.stops || []) as RouteViewModelStop[]);
  const focusedStopNumeric =
    focusedStopId != null && Number.isFinite(Number(focusedStopId)) && Number(focusedStopId) > 0
      ? Number(focusedStopId)
      : null;

  useEffect(() => {
    if (focusedStopNumeric == null) return;
    const timer = window.setTimeout(() => {
      const el = document.querySelector(`[data-route-stop-id="${focusedStopNumeric}"]`);
      el?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 120);
    return () => window.clearTimeout(timer);
  }, [focusedStopNumeric, stops.length]);
  if (isSequentialStopsReadModeEnabled()) {
    logSequentialStopsReadShadow({ context: 'RouteStructureBlock', stops });
  }

  const presentationCtx = buildRouteStopPresentationContext({
    applications: applications as RouteStopPresentationContext['applications'],
    loadingWarehouse: loadingWarehouse as RouteStopPresentationContext['loadingWarehouse'],
    unloadingWarehouse: unloadingWarehouse as RouteStopPresentationContext['unloadingWarehouse'],
    routeLoadingCity,
    routeUnloadingCity
  });
  const directionFromStops = buildRouteDirectionLabel(stops, {
    routeLoadingCity,
    routeUnloadingCity,
    loadingWarehouse: loadingWarehouse as { id?: number; city?: string | null } | null,
    unloadingWarehouse: unloadingWarehouse as { id?: number; city?: string | null } | null
  });
  const displayDirectionLabel =
    directionFromStops !== 'Не указано' ? directionFromStops : routeDirectionLabel?.trim() || '—';
  const summaryText = structureSummaryLine(displayDirectionLabel, stops.length);

  const unloadingStops = stops.filter((s) => s.type === 'unloading');
  const maxUnloadingPosition =
    unloadingStops.length > 0
      ? Math.max(...unloadingStops.map((s) => Number(s.position ?? 0)))
      : 0;

  const cs = model.cargoSummary;

  const body = (
    <>
      {showCargoSummary ? (
        <Row gutter={16} style={{ marginBottom: 16 }}>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center', backgroundColor: '#e6f7ff', border: '1px solid #91d5ff' }}>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#1890ff' }}>{cs.lineCount}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Позиций груза</div>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center', backgroundColor: '#fff7e6', border: '1px solid #ffd591' }}>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#fa8c16' }}>{cs.totalQuantityPlaces}</div>
              <div style={{ fontSize: 12, color: '#666' }}>Мест</div>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f6ffed', border: '1px solid #b7eb8f' }}>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#52c41a' }}>
                {cs.totalWeightKg.toLocaleString('ru-RU', { maximumFractionDigits: 1 })}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>Вес, кг</div>
            </Card>
          </Col>
          <Col span={6}>
            <Card size="small" style={{ textAlign: 'center', backgroundColor: '#f9f0ff', border: '1px solid #d3adf7' }}>
              <div style={{ fontSize: 20, fontWeight: 'bold', color: '#722ed1' }}>
                {cs.totalVolumeM3.toLocaleString('ru-RU', { maximumFractionDigits: 2 })}
              </div>
              <div style={{ fontSize: 12, color: '#666' }}>Объём, м³</div>
            </Card>
          </Col>
        </Row>
      ) : null}

      <Text strong style={{ display: 'block', marginBottom: 8 }}>Точки маршрута</Text>
      <DataTable
        size="small"
        pagination={false}
        rowKey={(r) => String((r as RouteViewModelStop).id)}
        dataSource={stops}
        onRow={(record) => {
          const stopId = Number((record as RouteViewModelStop).id);
          const isFocused = focusedStopNumeric != null && stopId === focusedStopNumeric;
          return {
            'data-route-stop-id': stopId,
            style: isFocused
              ? { backgroundColor: '#e6f4ff', outline: '1px solid #91caff' }
              : undefined
          };
        }}
        columns={[
          {
            title: '#',
            dataIndex: 'position',
            width: 50,
            render: (pos: unknown) =>
              pos != null && Number.isFinite(Number(pos)) ? Number(pos) + 1 : '—'
          },
          {
            title: 'Тип',
            dataIndex: 'type',
            width: 110,
            render: (t: string) =>
              t === 'loading' ? <Tag color="blue">Погрузка</Tag> : <Tag color="green">Выгрузка</Tag>
          },
          {
            title: 'Источник',
            key: 'src',
            width: 200,
            render: (_: unknown, record: RouteViewModelStop) => {
              const presentation = buildRouteStopPresentation(
                record as RouteStopPresentationInput,
                presentationCtx
              );
              const { primary, secondary } = formatRouteStopSourceDisplay(presentation);
              return (
                <div>
                  <Text strong style={{ fontSize: 13 }}>
                    {primary}
                  </Text>
                  {secondary ? (
                    <div>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        {secondary}
                      </Text>
                    </div>
                  ) : null}
                </div>
              );
            }
          },
          {
            title: 'Город',
            key: 'city',
            width: 120,
            render: (_: unknown, record: RouteViewModelStop) => {
              const address = stopFullAddress(
                record,
                applications,
                loadingWarehouse,
                unloadingWarehouse,
                record.type === 'unloading' ? 'unloading' : 'loading'
              );
              const storedAddress =
                record.address != null && String(record.address).trim()
                  ? String(record.address).trim()
                  : null;
              const resolvedAddress = storedAddress ?? (address !== '—' ? address : null);
              return buildRouteStopPresentation(
                { ...(record as RouteStopPresentationInput), address: resolvedAddress },
                presentationCtx
              ).displayCity;
            }
          },
          {
            title: 'Адрес',
            key: 'address',
            ellipsis: true,
            render: (_: unknown, record: RouteViewModelStop) =>
              stopFullAddress(
                record,
                applications,
                loadingWarehouse,
                unloadingWarehouse,
                record.type === 'unloading' ? 'unloading' : 'loading'
              )
          },
          {
            title: 'Контакт',
            dataIndex: 'contactPerson',
            width: 140,
            render: (v: unknown) => (v ? String(v) : '—')
          },
          {
            title: 'Телефон',
            dataIndex: 'phone',
            width: 120,
            render: (v: unknown) => (v ? String(v) : '—')
          },
          {
            title: 'План',
            dataIndex: 'plannedAt',
            width: 100,
            render: (d: unknown) => (d ? dayjs(String(d)).format('DD.MM.YYYY') : '—')
          },
          {
            title: 'Факт',
            dataIndex: 'actualAt',
            width: 100,
            render: (_d: unknown, record: RouteViewModelStop) => {
              const value = getStopExecutionActualAt(record);
              return value ? dayjs(String(value)).format('DD.MM.YYYY') : '—';
            }
          },
          ...(stopExecutionIndex
            ? [
                {
                  title: 'Исполнение',
                  key: 'execution',
                  width: 130,
                  render: (_: unknown, record: RouteViewModelStop) => {
                    const id = Number(record.id);
                    const ex = stopExecutionIndex.get(id);
                    return ex ? <Tag>{ex.label}</Tag> : '—';
                  }
                }
              ]
            : []),
          {
            title: 'Действия',
            key: 'actions',
            width: 200,
            render: (_: unknown, record: RouteViewModelStop) => {
              const id = Number(record.id);
              if (!Number.isFinite(id)) return null;
              const isLoadingType = record.type === 'loading';
              const canOperate = routeStatus === 'in_transit';
              const ex = stopExecutionIndex?.get(id);
              const executionState = getStopExecutionState(record);
              const isProcessed = ex
                ? ex.isProcessed
                : executionState === 'LOADED' || executionState === 'UNLOADED';
              const isMainUnloadingPoint =
                !isLoadingType && Number(record.position ?? 0) === maxUnloadingPosition;

              return (
                <Space>
                  {isLoadingType ? (
                    <Button
                      type="primary"
                      size="small"
                      loading={loadingStop}
                      disabled={!canOperate || isProcessed}
                      onClick={() => onRequestLoad(id)}
                    >
                      {isProcessed ? 'Погружено' : 'Погрузить'}
                    </Button>
                  ) : (
                    !isMainUnloadingPoint && (
                      <Button
                        type="primary"
                        size="small"
                        loading={unloadingStop}
                        disabled={!canOperate || isProcessed}
                        onClick={() => onRequestUnload(id)}
                      >
                        {isProcessed ? 'Выгружено' : 'Выгрузить'}
                      </Button>
                    )
                  )}
                </Space>
              );
            }
          }
        ]}
      />
    </>
  );

  return (
    <Card
      size="small"
      title="Структура маршрута"
      extra={<Text type="secondary">{summaryText}</Text>}
      style={{ marginBottom: 16 }}
    >
      {body}
    </Card>
  );
};

export default RouteStructureBlock;
